import { useMemo, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Building2,
  ChevronDown,
  ChevronRight,
  Coins,
  Loader2,
  Save,
  Users,
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import {
  centsToDkkInput,
  formatDkkFromCents,
  parseDkkInputToCents,
  usePlatformBillingUsage,
  useSetClinicBillingRateOverride,
  useUpdatePlatformBillingRate,
  type BillingAccount,
  type PlatformBillingRate,
} from '@/hooks/usePlatformBilling';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { ErrorAlert } from '@/components/ui/ErrorAlert';

function rateLabel(rate: PlatformBillingRate, locale: string): string {
  return locale.startsWith('da') ? rate.label_da : rate.label_en;
}

function rateDescription(rate: PlatformBillingRate, locale: string): string | null {
  return locale.startsWith('da') ? rate.description_da : rate.description_en;
}

function DefaultRatesSection({
  rates,
  locale,
}: {
  rates: PlatformBillingRate[];
  locale: string;
}) {
  const { t } = useTranslation();
  const updateRate = useUpdatePlatformBillingRate();
  const [drafts, setDrafts] = useState<Record<string, string>>({});

  const monthlyRates = rates.filter((r) => r.billing_model === 'per_unit_monthly');
  const usageRates = rates.filter((r) => r.billing_model === 'usage');

  async function handleSave(metricKey: string) {
    const cents = parseDkkInputToCents(drafts[metricKey] ?? '');
    if (cents === null) {
      toast.error(t('admin.billing.invalid_price'));
      return;
    }

    try {
      await updateRate.mutateAsync({ metric_key: metricKey, unit_price_cents: cents });
      toast.success(t('admin.billing.rate_saved'));
      setDrafts((prev) => {
        const next = { ...prev };
        delete next[metricKey];
        return next;
      });
    } catch (err) {
      console.error('Failed to save billing rate', err);
      toast.error(t('admin.billing.save_error'));
    }
  }

  return (
    <div className="space-y-6">
      <section className="rounded-lg border border-gray-200 bg-white p-6 dark:border-gray-700 dark:bg-gray-800">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
          {t('admin.billing.default_rates_title')}
        </h2>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
          {t('admin.billing.default_rates_desc')}
        </p>

        <div className="mt-6 space-y-4">
          {monthlyRates.map((rate) => {
            const inputValue = drafts[rate.metric_key] ?? centsToDkkInput(rate.unit_price_cents);
            const hasDraft = rate.metric_key in drafts;

            return (
              <form
                key={rate.metric_key}
                className="flex flex-col gap-3 rounded-lg border border-gray-100 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-900/40 sm:flex-row sm:items-end sm:justify-between"
                onSubmit={(e: FormEvent) => {
                  e.preventDefault();
                  void handleSave(rate.metric_key);
                }}
              >
                <div className="flex-1">
                  <p className="font-medium text-gray-900 dark:text-white">
                    {rateLabel(rate, locale)}
                  </p>
                  {rateDescription(rate, locale) && (
                    <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                      {rateDescription(rate, locale)}
                    </p>
                  )}
                </div>
                <div className="flex items-end gap-2">
                  <div>
                    <label className="mb-1 block text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
                      {t('admin.billing.price_per_month')}
                    </label>
                    <div className="relative">
                      <Input
                        type="text"
                        inputMode="decimal"
                        value={inputValue}
                        onChange={(e) =>
                          setDrafts((prev) => ({ ...prev, [rate.metric_key]: e.target.value }))
                        }
                        className="w-32 pr-10"
                      />
                      <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-sm text-gray-400">
                        kr
                      </span>
                    </div>
                  </div>
                  <Button
                    type="submit"
                    disabled={updateRate.isPending || !hasDraft}
                    size="sm"
                  >
                    {updateRate.isPending ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <>
                        <Save className="mr-1 h-4 w-4" />
                        {t('admin.billing.save')}
                      </>
                    )}
                  </Button>
                </div>
              </form>
            );
          })}
        </div>
      </section>

      {usageRates.length > 0 && (
        <section className="rounded-lg border border-dashed border-gray-300 bg-white p-6 dark:border-gray-600 dark:bg-gray-800">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
            {t('admin.billing.usage_rates_title')}
          </h2>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            {t('admin.billing.usage_rates_desc')}
          </p>
          <ul className="mt-4 space-y-3">
            {usageRates.map((rate) => (
              <li
                key={rate.metric_key}
                className="flex items-center justify-between rounded-md bg-gray-50 px-4 py-3 dark:bg-gray-900/40"
              >
                <div>
                  <p className="font-medium text-gray-900 dark:text-white">
                    {rateLabel(rate, locale)}
                  </p>
                  {rateDescription(rate, locale) && (
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      {rateDescription(rate, locale)}
                    </p>
                  )}
                </div>
                <span className="rounded-full bg-amber-100 px-2.5 py-1 text-xs font-medium text-amber-800 dark:bg-amber-900/40 dark:text-amber-300">
                  {t('admin.billing.coming_soon')}
                </span>
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}

function AccountOverrideRow({
  account,
}: {
  account: BillingAccount;
}) {
  const { t } = useTranslation();
  const setOverride = useSetClinicBillingRateOverride();
  const [expanded, setExpanded] = useState(false);
  const [clinicDraft, setClinicDraft] = useState('');
  const [employeeDraft, setEmployeeDraft] = useState('');

  const hasClinicOverride = account.overrides.clinic != null;
  const hasEmployeeOverride = account.overrides.employee != null;

  async function saveOverride(metricKey: 'clinic' | 'employee', draft: string, clear: boolean) {
    const cents = clear ? null : parseDkkInputToCents(draft);
    if (!clear && cents === null) {
      toast.error(t('admin.billing.invalid_price'));
      return;
    }

    try {
      await setOverride.mutateAsync({
        billing_account_id: account.billing_account_id,
        metric_key: metricKey,
        unit_price_cents: cents,
      });
      toast.success(t('admin.billing.override_saved'));
      if (metricKey === 'clinic') setClinicDraft('');
      if (metricKey === 'employee') setEmployeeDraft('');
    } catch (err) {
      console.error('Failed to save override', err);
      toast.error(t('admin.billing.save_error'));
    }
  }

  return (
    <>
      <tr className="hover:bg-gray-50 dark:hover:bg-gray-700/30">
        <td className="whitespace-nowrap px-4 py-4">
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            className="flex items-center gap-2 text-left font-medium text-gray-900 dark:text-white"
          >
            {expanded ? (
              <ChevronDown className="h-4 w-4 text-gray-400" />
            ) : (
              <ChevronRight className="h-4 w-4 text-gray-400" />
            )}
            {account.root_clinic_name}
          </button>
        </td>
        <td className="whitespace-nowrap px-4 py-4 text-sm text-gray-600 dark:text-gray-300">
          {account.clinic_count}
          {account.sub_clinic_count > 0 && (
            <span className="ml-1 text-gray-400">
              ({t('admin.billing.including_subs', { count: account.sub_clinic_count })})
            </span>
          )}
        </td>
        <td className="whitespace-nowrap px-4 py-4 text-sm text-gray-600 dark:text-gray-300">
          {account.employee_count}
        </td>
        <td className="whitespace-nowrap px-4 py-4 text-sm text-gray-600 dark:text-gray-300">
          {formatDkkFromCents(account.effective_rates.clinic ?? 0)}
          {hasClinicOverride && (
            <span className="ml-1 text-xs text-amber-600 dark:text-amber-400">
              ({t('admin.billing.custom')})
            </span>
          )}
        </td>
        <td className="whitespace-nowrap px-4 py-4 text-sm text-gray-600 dark:text-gray-300">
          {formatDkkFromCents(account.effective_rates.employee ?? 0)}
          {hasEmployeeOverride && (
            <span className="ml-1 text-xs text-amber-600 dark:text-amber-400">
              ({t('admin.billing.custom')})
            </span>
          )}
        </td>
        <td className="whitespace-nowrap px-4 py-4 text-right text-sm font-semibold text-gray-900 dark:text-white">
          {formatDkkFromCents(account.estimated_monthly_cents)}
        </td>
      </tr>
      {expanded && (
        <tr className="bg-gray-50 dark:bg-gray-900/30">
          <td colSpan={6} className="px-4 py-4">
            <div className="space-y-4">
              <div>
                <p className="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
                  {t('admin.billing.clinics_in_group')}
                </p>
                <ul className="mt-2 flex flex-wrap gap-2">
                  {account.clinics.map((clinic) => (
                    <li
                      key={clinic.id}
                      className="rounded-full bg-white px-3 py-1 text-xs text-gray-700 ring-1 ring-gray-200 dark:bg-gray-800 dark:text-gray-200 dark:ring-gray-600"
                    >
                      {clinic.name}
                      {clinic.is_sub_clinic && (
                        <span className="ml-1 text-gray-400">{t('admin.billing.sub_clinic')}</span>
                      )}
                    </li>
                  ))}
                </ul>
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                <div className="rounded-lg border border-gray-200 bg-white p-4 dark:border-gray-600 dark:bg-gray-800">
                  <p className="text-sm font-medium text-gray-900 dark:text-white">
                    {t('admin.billing.clinic_price_override')}
                  </p>
                  <div className="mt-2 flex items-end gap-2">
                    <Input
                      type="text"
                      inputMode="decimal"
                      placeholder={
                        hasClinicOverride
                          ? centsToDkkInput(account.overrides.clinic!)
                          : centsToDkkInput(account.effective_rates.clinic ?? 0)
                      }
                      value={clinicDraft}
                      onChange={(e) => setClinicDraft(e.target.value)}
                      className="flex-1"
                    />
                    <Button
                      size="sm"
                      disabled={setOverride.isPending || !clinicDraft}
                      onClick={() => void saveOverride('clinic', clinicDraft, false)}
                    >
                      {t('admin.billing.save')}
                    </Button>
                    {hasClinicOverride && (
                      <Button
                        size="sm"
                        variant="secondary"
                        disabled={setOverride.isPending}
                        onClick={() => void saveOverride('clinic', '', true)}
                      >
                        {t('admin.billing.reset')}
                      </Button>
                    )}
                  </div>
                </div>

                <div className="rounded-lg border border-gray-200 bg-white p-4 dark:border-gray-600 dark:bg-gray-800">
                  <p className="text-sm font-medium text-gray-900 dark:text-white">
                    {t('admin.billing.employee_price_override')}
                  </p>
                  <div className="mt-2 flex items-end gap-2">
                    <Input
                      type="text"
                      inputMode="decimal"
                      placeholder={
                        hasEmployeeOverride
                          ? centsToDkkInput(account.overrides.employee!)
                          : centsToDkkInput(account.effective_rates.employee ?? 0)
                      }
                      value={employeeDraft}
                      onChange={(e) => setEmployeeDraft(e.target.value)}
                      className="flex-1"
                    />
                    <Button
                      size="sm"
                      disabled={setOverride.isPending || !employeeDraft}
                      onClick={() => void saveOverride('employee', employeeDraft, false)}
                    >
                      {t('admin.billing.save')}
                    </Button>
                    {hasEmployeeOverride && (
                      <Button
                        size="sm"
                        variant="secondary"
                        disabled={setOverride.isPending}
                        onClick={() => void saveOverride('employee', '', true)}
                      >
                        {t('admin.billing.reset')}
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

export function AdminBilling() {
  const { t, i18n } = useTranslation();
  const { data, isLoading, error } = usePlatformBillingUsage();

  const sortedAccounts = useMemo(
    () => [...(data?.accounts ?? [])].sort((a, b) => b.estimated_monthly_cents - a.estimated_monthly_cents),
    [data?.accounts],
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
      </div>
    );
  }

  if (error || !data) {
    return <ErrorAlert message={t('admin.billing.load_error')} />;
  }

  const totals = data.platform_totals;

  return (
    <div className="mx-auto max-w-6xl space-y-8 p-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
          {t('admin.billing.title')}
        </h1>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
          {t('admin.billing.subtitle')}
        </p>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <div className="rounded-lg border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800">
          <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
            <Building2 className="h-4 w-4" />
            {t('admin.billing.total_billing_accounts')}
          </div>
          <p className="mt-2 text-2xl font-bold text-gray-900 dark:text-white">
            {totals.billing_accounts}
          </p>
        </div>
        <div className="rounded-lg border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800">
          <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
            <Building2 className="h-4 w-4" />
            {t('admin.billing.total_clinics')}
          </div>
          <p className="mt-2 text-2xl font-bold text-gray-900 dark:text-white">
            {totals.clinic_count}
          </p>
        </div>
        <div className="rounded-lg border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800">
          <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
            <Users className="h-4 w-4" />
            {t('admin.billing.total_employees')}
          </div>
          <p className="mt-2 text-2xl font-bold text-gray-900 dark:text-white">
            {totals.employee_count}
          </p>
        </div>
        <div className="rounded-lg border border-purple-200 bg-purple-50 p-4 dark:border-purple-800 dark:bg-purple-900/20">
          <div className="flex items-center gap-2 text-sm text-purple-700 dark:text-purple-300">
            <Coins className="h-4 w-4" />
            {t('admin.billing.estimated_mrr')}
          </div>
          <p className="mt-2 text-2xl font-bold text-purple-900 dark:text-purple-100">
            {formatDkkFromCents(totals.estimated_monthly_cents)}
          </p>
        </div>
      </div>

      <DefaultRatesSection rates={data.rates} locale={i18n.language} />

      <section className="rounded-lg border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800">
        <div className="border-b border-gray-200 px-6 py-4 dark:border-gray-700">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
            {t('admin.billing.accounts_title')}
          </h2>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            {t('admin.billing.accounts_desc')}
          </p>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-700/50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                  {t('admin.billing.col_account')}
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                  {t('admin.billing.col_clinics')}
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                  {t('admin.billing.col_employees')}
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                  {t('admin.billing.col_clinic_rate')}
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                  {t('admin.billing.col_employee_rate')}
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                  {t('admin.billing.col_estimated')}
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {sortedAccounts.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-4 py-8 text-center text-sm text-gray-500 dark:text-gray-400">
                    {t('admin.billing.no_accounts')}
                  </td>
                </tr>
              ) : (
                sortedAccounts.map((account) => (
                  <AccountOverrideRow
                    key={account.billing_account_id}
                    account={account}
                  />
                ))
              )}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}
